import { ReactNode, useState, isValidElement, forwardRef } from "react";
import {
  Box,
  Input as ChakraInput,
  InputGroup,
  InputProps as ChakraInputProps,
  InputRightElement,
  Stack,
  Text,
  useStyleConfig,
} from "@chakra-ui/react";
import { FieldError } from "react-hook-form";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";

import { useLocalization } from "hooks/useLocalization";
import { TranslationKeys } from "utils/localization";
import { resolveChakraSpacing } from "./helpers";

export type InputTypes =
  | "button"
  | "checkbox"
  | "color"
  | "date"
  | "datetime-local"
  | "email"
  | "file"
  | "hidden"
  | "image"
  | "month"
  | "number"
  | "password"
  | "radio"
  | "range"
  | "reset"
  | "search"
  | "submit"
  | "tel"
  | "text"
  | "time"
  | "url"
  | "week";

type InputProps = Omit<ChakraInputProps, "type"> & {
  type?: InputTypes;
  label?: ReactNode;
  error?: FieldError;
  customMessage?: ReactNode;
  rightElement?: ReactNode;
  required?: boolean;
  dataTest?: string;
  customError?: TranslationKeys;
  noNegative?: boolean;
  flagInput?: ReactNode | undefined;
  handleFocus?: () => void;
};
// /**
//  * @deprecated Use out new AnimatedInput instead
//  */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      customMessage,
      type,
      rightElement,
      required,
      dataTest,
      customError,
      noNegative,
      flagInput,
      handleFocus,
      ...props
    },
    ref,
  ) => {
    const inputStyles = useStyleConfig("CustomInput", {});
    const [showPassword, setShowPassword] = useState(false);
    const { translate } = useLocalization();

    const newProps = resolveChakraSpacing<InputProps, InputProps>(props);

    const showRightContent = rightElement || type === "password";

    const getLabel = () => {
      if (!label) {
        return null;
      }

      if (isValidElement(label)) {
        return label;
      }

      let color = "ud-grey-600";
      if (Boolean(error)) {
        color = "ud-error";
      } else if (props.isDisabled) {
        color = "ud-grey-300";
      }

      return (
        <Text
          variant="form-label"
          textAlign="left"
          color={color}
          data-test="global.writeBox.text"
        >
          {label}
          {required && (
            <Text as="span" pl="3px" color="ud-error">
              *
            </Text>
          )}
        </Text>
      );
    };

    return (
      <Box {...newProps.spacing} data-test="global.writeBox">
        {getLabel()}
        <InputGroup onClick={() => handleFocus && handleFocus()}>
          <ChakraInput
            ref={ref}
            type={type === "password" && showPassword ? "text" : type}
            _focusVisible={{ borderColor: "ud-grey-500" }}
            sx={inputStyles}
            borderColor={Boolean(error) ? "ud-error" : "ud-grey-300"}
            data-test={dataTest}
            onInput={(e) => {
              if (
                type === "number" &&
                noNegative &&
                parseInt(e.currentTarget.value) < 0
              ) {
                e.currentTarget.value = "0";
              }
            }}
            onChange={newProps.rest.onChange}
            name={newProps.rest.name}
            {...newProps.rest}
          />
          {showRightContent && (
            <InputRightElement
              aria-label="Zobrazit heslo"
              data-test="global.showPass"
            >
              <Stack direction="row" align="center" spacing="5px">
                {type === "password" && (
                  <Stack
                    _hover={{ cursor: "pointer" }}
                    onClick={() => setShowPassword((p) => !p)}
                  >
                    {!showPassword ? (
                      <ViewIcon color="ud-grey-300" />
                    ) : (
                      <ViewOffIcon color="ud-grey-300" />
                    )}
                  </Stack>
                )}
                {rightElement}
              </Stack>
            </InputRightElement>
          )}
          {flagInput}
        </InputGroup>
        {error?.message && (
          <Text variant="form-error">
            {customError ? translate(customError) : error.message}
          </Text>
        )}
        {customMessage && <Text variant="form-message">{customMessage}</Text>}
      </Box>
    );
  },
);

export default Input;
